import type { CategoryMeta, TemplateCategory } from "../types";
import { defineComponent } from "vue";
import Button from "@/components/ui/button/Button";
import { $t } from "@/services/i18n";
import { p } from "@/utils/props-builder";

const props = {
  categories: p.$array<CategoryMeta>(),
  selectedCategory: p.$string<TemplateCategory | "all">(),
  templateCounts: p.$object<Record<TemplateCategory | "all", number>>(),
  onSelect: p.$func<(category: TemplateCategory | "all") => void>(),
  onStartFromScratch: p.$func<() => void>(),
};

export default defineComponent({
  name: "CategorySidebar",
  props,
  setup(props) {
    return () => (
      <div class="flex w-72 shrink-0 flex-col border-r p-4">
        <div class="mb-4">
          <h3 class="text-sm font-medium text-muted-foreground">
            {$t("agents:templates.categories_title")}
          </h3>
        </div>
        <div class="space-y-1">
          {/* Opção "Todos" — neutra quando inativa, colorida (accent) quando ativa */}
          {(() => {
            const isSelected = props.selectedCategory === "all";
            return (
              <Button
                variant={isSelected ? "light" : "ghost"}
                class={[
                  "h-9 w-full justify-start",
                  isSelected
                    ? "**:data-[slot=icon]:text-accent-11"
                    : "text-muted-foreground **:data-[slot=icon]:text-muted-foreground",
                ]}
                icon="lucide:layout-grid"
                onClick={() => props.onSelect("all")}
              >
                <span class="flex-1 text-left">{$t("agents:templates.categories.all")}</span>
                <span class="ml-2 text-xs text-muted-foreground">{props.templateCounts?.all}</span>
              </Button>
            );
          })()}

          {/* Categorias — sem cor quando inativas; só recebem a cor da categoria ao ativar */}
          {props.categories?.map((category) => {
            const isSelected = props.selectedCategory === category.id;
            return (
              <Button
                key={category.id}
                variant={isSelected ? "light" : "ghost"}
                class={[
                  "h-9 w-full justify-start",
                  isSelected
                    ? "**:data-[slot=icon]:text-accent-11"
                    : "text-muted-foreground **:data-[slot=icon]:text-muted-foreground",
                ]}
                icon={category.icon}
                data-accent={isSelected ? category.color : undefined}
                onClick={() => props.onSelect(category.id)}
              >
                <span class="flex-1 text-left">{$t(category.nameKey as any)}</span>
                <span class="ml-2 text-xs text-muted-foreground">
                  {props.templateCounts?.[category.id]}
                </span>
              </Button>
            );
          })}
        </div>

        {/* Separador e opção "Começar do Zero" - fixo no final */}
        <div class="mt-auto border-t pt-4">
          <Button class="w-full" icon="lucide:plus" onClick={props.onStartFromScratch}>
            {$t("agents:templates.start_from_scratch")}
          </Button>
        </div>
      </div>
    );
  },
});
